import React from 'react';
import {Formik, FormikProps} from 'formik';
import {useDispatch} from 'react-redux';

import {errorMessage} from 'shared/actions/notifications';
import {saveTestScore} from 'shared/portfolio/actions/test-scores';

export type TestScoreType =
  | 'psat2015'
  | 'sat2016'
  | 'preAct'
  | 'act'
  | 'act2025'
  | 'ap'
  | 'actPlan'
  | 'psat'
  | 'other';

export interface TestScore {
  _id: string;
  type: TestScoreType;
  month: number;
  year: number;
  name?: string;
  subject?: string;
  subtype: {
    name?: string;
    abbreviation?: string;
    subject?: string;
  };
  scores: Record<string, number>;
  maxScore?: number;
  abbreviation?: string;
  createdAt: number;
  updatedAt: number;

  // not from the API, FE only
  editing?: boolean;
  new?: boolean;
  scrollTo?: boolean;
}

export type TestScoreForm = Omit<
  TestScore,
  'createdAt' | 'updatedAt' | 'type' | 'month' | 'year' | 'scores'
> & {
  type: TestScoreType | null;
  month: number | '';
  year: number | '';
  scores: Record<string, number | ''>;
};

const OPTIONAL_SCORE_FIELDS: Partial<Record<TestScoreType, string[]>> = {
  act2025: ['act2025ScoreScience'],
};

const SCORE_RANGES: Partial<
  Record<
    TestScoreType,
    {
      min: number;
      max: number;
      step: number;
      customRanges?: Record<string, {min: number; max: number; step: number}>;
    }
  >
> = {
  act: {min: 1, max: 36, step: 1},
  act2025: {min: 1, max: 36, step: 1},
  actPlan: {min: 1, max: 32, step: 1},
  preAct: {min: 1, max: 35, step: 1},
  ap: {min: 1, max: 5, step: 1},
  psat: {min: 20, max: 80, step: 1},
  psat2015: {min: 160, max: 760, step: 10},
  sat2016: {
    min: 200,
    max: 800,
    step: 10,
    customRanges: {
      sat2016ScoreReadingAndWriting: {min: 200, max: 800, step: 10},
      sat2016ScoreMathematics: {min: 200, max: 800, step: 10},
    },
  },
  other: {min: 0, max: 1000000, step: 1},
};

interface Props {
  initialValues: TestScoreForm;
  children: (props: FormikProps<TestScoreForm>) => React.ReactNode;
  onSuccess?: () => void;
}

const TestScoresFormWrapper = ({initialValues, children, onSuccess}: Props) => {
  const dispatch = useDispatch();

  return (
    <Formik<TestScoreForm>
      initialValues={initialValues}
      validate={(values) => {
        const errors: Partial<Record<keyof TestScoreForm, string | Record<string, string>>> = {};

        const optionalScoreFields = values.type ? OPTIONAL_SCORE_FIELDS[values.type] ?? [] : [];
        const scoreRange = values.type ? SCORE_RANGES[values.type] : undefined;

        Object.keys(values.scores).forEach((scoreName) => {
          const value = values.scores[scoreName];
          const isOptional = optionalScoreFields.includes(scoreName);

          if (!value && value !== 0 && !isOptional) {
            if (!errors.scores) {
              errors.scores = {};
            }

            errors.scores[scoreName] = 'Score required.';
          } else if (typeof value === 'number' && scoreRange) {
            const range = scoreRange.customRanges?.[scoreName] || scoreRange;
            if (value < range.min || value > range.max || value % range.step !== 0) {
              if (!errors.scores) {
                errors.scores = {};
              }

              if (isOptional) {
                errors.scores[scoreName] = `Must be between ${range.min}–${range.max}${
                  range.step > 1 ? ` in increments of ${range.step}` : ''
                }, or leave blank if not taken.`;
              } else {
                errors.scores[scoreName] = `Must be between ${range.min}–${range.max}${
                  range.step > 1 ? ` in increments of ${range.step}` : ''
                }.`;
              }
            }
          }
        });

        if (!values.type) {
          errors.type = 'Test type is required.';
        }

        if (values.type === 'ap' && !values.subject) {
          errors.subject = 'Subject is required.';
        } else if (values.type === 'other') {
          if (!values.name) {
            errors.name = 'Test name is required.';
          } else if (values.name.length > 256) {
            errors.name = 'Test name too long (256 characters max).';
          }
        }

        if (!values.month && values.month !== 0) {
          errors.month = 'Month is required.';
        }

        if (!values.year) {
          errors.year = 'Year is required.';
        }

        if (
          values.year &&
          values.month !== '' &&
          new Date(values.year, values.month) > new Date()
        ) {
          errors.month = 'Date must not be in the future.';
        }

        return errors;
      }}
      onSubmit={async (values) => {
        try {
          await dispatch(saveTestScore(values));
          onSuccess?.();
        } catch {
          dispatch(errorMessage());
        }
      }}
      component={children}
    />
  );
};

export default TestScoresFormWrapper;
